import React, { useState } from 'react';
import { API_URL } from '../config';

interface ModifyFlowPanelProps {
  currentFlow: any;
  onModified: (modifiedFlow: any) => void;
  onClose: () => void;
}

const ModifyFlowPanel: React.FC<ModifyFlowPanelProps> = ({
  currentFlow,
  onModified,
  onClose
}) => {
  const [instructions, setInstructions] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const handleModify = async () => {
    if (!instructions.trim()) {
      setError('Inserisci le istruzioni di modifica');
      return;
    }
    
    if (!currentFlow || !currentFlow.nodes || currentFlow.nodes.length === 0) {
      setError('Nessun flow caricato da modificare');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('✏️ Invio richiesta di modifica flow...');
      
      const response = await fetch(`${API_URL}/flow/modify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          flow_definition: currentFlow,
          modification_instructions: instructions,
          llm_provider: 'groq',
          model: 'llama-3.3-70b-versatile'
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ Flow modificato:', data);
      
      if (data.success && data.flow) {
        onModified(data.flow);
        setInstructions(''); // Reset
      } else {
        throw new Error('Risposta API non valida');
      }
    } catch (err: any) {
      console.error('❌ Errore modifica flow:', err);
      setError(err.message || 'Errore nella modifica del flow');
    } finally {
      setLoading(false);
    }
  };
  
  const exampleInstructions = [
    'Rendi il messaggio di benvenuto più caloroso e amichevole',
    'Aggiungi un nodo SAY di conferma dopo ogni input',
    'Cambia il messaggio finale con un ringraziamento più dettagliato',
    'Rimuovi la condizione e rendi il flusso lineare',
    'Aggiungi emoji appropriate in tutti i messaggi',
  ];
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <span className="text-3xl">✏️</span>
            <div>
              <h2 className="text-2xl font-bold">Modifica Flow con AI</h2>
              <p className="text-sm text-amber-100">
                Descrivi come vuoi modificare il flow corrente
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
            title="Chiudi"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Flow Info */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 text-blue-800 mb-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-semibold">Flow corrente</span>
            </div>
            <div className="text-sm text-blue-700">
              {currentFlow && currentFlow.nodes ? (
                <>
                  <span className="font-mono">ID: {currentFlow.id || 'N/A'}</span>
                  <span className="mx-2">•</span>
                  <span>{currentFlow.nodes.length} nodi</span>
                  <span className="mx-2">•</span>
                  <span>{currentFlow.edges?.length || 0} connessioni</span>
                </>
              ) : (
                <span className="text-orange-600">⚠️ Nessun flow caricato</span>
              )}
            </div>
          </div>
          
          {/* Instructions Input */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              🎯 Istruzioni di Modifica
            </label>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Esempio: Aggiungi emoji in tutti i messaggi e rendi il tono più amichevole"
              className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none font-mono text-sm"
              disabled={loading}
            />
            <div className="text-xs text-gray-500 mt-1">
              {instructions.length} caratteri
            </div>
          </div>
          
          {/* Examples */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              💡 Esempi di modifiche
            </label>
            <div className="space-y-2">
              {exampleInstructions.map((example, idx) => (
                <button
                  key={idx}
                  onClick={() => setInstructions(example)}
                  className="w-full text-left px-4 py-2 bg-gray-50 hover:bg-amber-50 border border-gray-200 hover:border-amber-300 rounded-lg text-sm transition-colors"
                  disabled={loading}
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
          
          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-2 text-red-800">
                <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <div className="font-semibold mb-1">Errore</div>
                  <div className="text-sm">{error}</div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-between items-center">
          <div className="text-xs text-gray-500">
            💡 Il flow verrà modificato preservando la struttura esistente
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              disabled={loading}
            >
              Annulla
            </button>
            <button
              onClick={handleModify}
              disabled={loading || !instructions.trim()}
              className="px-6 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium flex items-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Modificando...</span>
                </>
              ) : (
                <>
                  <span>✏️</span>
                  <span>Modifica Flow</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModifyFlowPanel;

